/**
 * <ActivationPriceSummary> — le récapitulatif tarifaire du composeur (organism).
 *
 * Il ferme l'écran, recalculé à chaque changement : **prix affiché = prix facturé**. Tout arrive
 * déjà formaté — le composant n'additionne rien, ne proratise rien, ne convertit rien.
 *
 * La ligne du 1ᵉʳ mois ne se rend que lorsqu'elle DIFFÈRE du mensuel, et elle se peint en vert :
 * c'est une remise ou un prorata, pas une seconde facture. Son libellé dit ce qui la produit
 * (« prorata », « remise fidélité », « −50 €/mois sur 3 mois ») — c'est le view-model qui le
 * compose, comme le legacy.
 *
 * Sans récapitulatif, l'offre n'est pas calculable : on le dit, et l'écran bloque sa confirmation.
 * Jamais « 0 € », qui se lirait comme la gratuité.
 */
import React from "react"

import { Card } from "../../molecules/Card"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SummaryRow } from "../../molecules/SummaryRow"
import { Text } from "../../atoms/typography/Text"
import { type UIActivationPriceSummary } from "../../../ports/primary/ui/types"

/** La phrase du legacy quand la grille ne donne pas de prix pour la formule retenue. */
const UNCOMPUTABLE =
    "Cette formule n'est pas disponible en self-service pour le moment. Contactez le support pour l'activer."

export type ActivationPriceSummaryProps = {
    /** Le récapitulatif. Absent → l'offre n'est pas calculable. */
    summary?: UIActivationPriceSummary | null
    testID?: string
}

export function ActivationPriceSummary(props: ActivationPriceSummaryProps) {
    const { summary, testID } = props

    if (summary == null) {
        return (
            <Card background="surface-subtle" p="4" testID={testID}>
                <Text size="text-md" color="text-muted">
                    {UNCOMPUTABLE}
                </Text>
            </Card>
        )
    }

    const { monthlyPrice, hoursLine, hourlyLine, firstMonthLabel, firstMonthPrice, resumeLine } = summary
    const hasDetail = hoursLine != null || hourlyLine != null

    return (
        <Card background="surface-subtle" p="4" testID={testID}>
            <Stack gap="2">
                <SummaryRow label="Prix mensuel" value={monthlyPrice} emphasis />

                {hasDetail && (
                    <Row gap="4" justify="between" align="start">
                        <Text size="text-sm" color="text-muted">
                            {hoursLine ?? ""}
                        </Text>
                        <Text size="text-sm" color="text-muted">
                            {hourlyLine ?? ""}
                        </Text>
                    </Row>
                )}

                {firstMonthLabel != null && firstMonthPrice != null && (
                    <SummaryRow label={firstMonthLabel} value={firstMonthPrice} color="success" />
                )}

                {/* La date de reprise, et rien d'autre : les droits sont dits une fois, sous
                    « Durée d'engagement ». */}
                {resumeLine != null && resumeLine !== "" && (
                    <Text size="text-sm" color="text-muted">
                        {resumeLine}
                    </Text>
                )}
            </Stack>
        </Card>
    )
}
